/**
 * OBK-7 (K44) — durable "Needs Confirmation" operator queue.
 *
 * Renders the persisted, role-gated list of OptiQueue bookings awaiting shop
 * follow-up (driven by `tickets.confirmation_status`), so the alert is no
 * longer toast-only. Returns null for roles that may not view it — customer
 * contact PII is never rendered to unauthorized roles.
 *
 * CORE-BN (front-of-house attention capacity) — advisory only
 * ----------------------------------------------------------
 * Each row in this queue is a callback somebody at the front desk has to
 * make before the appointment is real. Whether those calls happen depends
 * on whether anyone at the desk has attention left, which this queue
 * previously knew nothing about — it counted what was owed while assuming
 * unlimited advisor attention (CORE-BN-GAP-002).
 *
 * The advisory line added below is exactly that and nothing more. It
 * cannot reject an appointment, refuse intake, reorder the queue, resolve
 * an item, or change what the operator may do — every button, every row
 * and every action below is untouched. When CORE-BN has no estimate the
 * assessment degrades to `'unknown'`, no line renders, and this component
 * behaves precisely as it did before.
 */
import { useNavigate } from 'react-router-dom';
import { Phone, Check, ExternalLink, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNeedsConfirmationQueue } from '@/hooks/useNeedsConfirmationQueue';
import { useFohAttentionCapacity } from '@/hooks/foh/useFohAttentionCapacity';
import { assessConfirmationFollowUpRisk } from '@/lib/foh/attention';
import {
  needsConfirmationLaneLabel,
  needsConfirmationOpenRoute,
} from '@/lib/optiqueue/needsConfirmationQueue';

interface NeedsConfirmationQueueProps {
  /** Optional: called after navigating to a ticket (e.g. to close a sheet). */
  onOpenTicket?: () => void;
}

export function NeedsConfirmationQueue({ onOpenTicket }: NeedsConfirmationQueueProps) {
  const { activeShop, membership } = useAuth();
  const navigate = useNavigate();
  const { items, canView, resolve } = useNeedsConfirmationQueue(
    activeShop?.id ?? null,
    membership?.role ?? null,
  );

  // CORE-BN. Hooks run unconditionally (before the early returns below) so
  // hook order is stable; the query itself is inert without the flag or a
  // shop, and the whole thing is display-only.
  const fohAttentionEnabled = useFeatureFlag('fohAttentionCapacity');
  const fohAttention = useFohAttentionCapacity({
    shopId: activeShop?.id ?? null,
    enabled: fohAttentionEnabled && canView,
  });
  const followUp = assessConfirmationFollowUpRisk({
    signal: fohAttention.signal,
    pendingCount: items.length,
  });

  // Role gate: unauthorized roles see nothing (no PII).
  if (!canView) return null;
  // Nothing awaiting confirmation → render nothing (keeps the panel quiet).
  if (items.length === 0) return null;

  // Advisory only, and only when there is something worth saying. An
  // absent estimate produces `message === null`, which renders the queue
  // exactly as it rendered before CORE-BN existed.
  const advisory = fohAttentionEnabled ? followUp.message : null;

  const openTicket = (id: string) => {
    navigate(needsConfirmationOpenRoute(id));
    onOpenTicket?.();
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-300/60 bg-amber-50/50 dark:bg-amber-950/20 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="h-4 w-4 text-amber-600" />
        <span className="font-medium text-sm">Needs Confirmation</span>
        <Badge variant="secondary">{items.length}</Badge>
      </div>
      {advisory && (
        <div
          className="mb-2 flex items-start gap-1.5 rounded-md border border-border/60 bg-background/60 p-2 text-xs text-muted-foreground"
          data-testid="foh-attention-followup-advisory"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            {advisory}{' '}
            <span className="opacity-80">
              Estimated front-of-house load for the shop — not a measure of
              anyone&apos;s performance.
            </span>
          </span>
        </div>
      )}
      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} className="rounded-md border bg-background p-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium truncate">{t.customer_name || 'Customer'}</span>
              <Badge variant="outline" className="shrink-0">
                {needsConfirmationLaneLabel(t.intent)}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {t.customer_phone || 'No phone'}
            </div>
            {t.customer_request_notes && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {t.customer_request_notes}
              </div>
            )}
            <div className="flex gap-2 mt-2" data-testid="needs-confirmation-actions">
              <Button size="sm" variant="ghost" onClick={() => openTicket(t.id)}>
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Open
              </Button>
              {t.confirmation_status === 'needs_contact' && (
                <Button size="sm" variant="outline" onClick={() => resolve(t.id, 'mark_contacted')}>
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  Mark Contacted
                </Button>
              )}
              {t.confirmation_status === 'contacted' && (
                <Button size="sm" onClick={() => resolve(t.id, 'confirm')}>
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Confirm
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
